/**
 * 手書きサインのポップアップ(2026-08-26)。
 * 支払先にタブレット・スマホ(タッチ)やPC(マウス)でその場で署名してもらう。
 * 「この署名で確定」で署名画像(PNGデータURL)を返す
 */
import { useEffect, useRef, useState } from 'react';
import Modal from '@core/components/Modal';

export default function SignaturePadModal({
  title,
  onClose,
  onDone,
}: {
  title: string;
  onClose: () => void;
  onDone: (dataUrl: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasStroke, setHasStroke] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    // 背景は塗らない=透過PNGにする(2026-08-27: 白背景が受領書の下線を隠していた)。
    // 画面上の白い見た目はcanvas要素のCSS背景で出す
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    canvasRef.current!.setPointerCapture(e.pointerId);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasStroke(true);
  }

  function up() {
    drawing.current = false;
  }

  function clear() {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    // 透過を保ったまま消去する
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
  }

  return (
    <Modal onClose={onClose} width="min(680px, 96vw)">
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <h3>{title}</h3>
          <div className="path" style={{ margin: '2px 0 0' }}>
            枠内に指またはマウスで署名してください(タブレット・スマホ対応)
          </div>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        width={1120}
        height={360}
        style={{
          width: '100%',
          height: 200,
          border: '2px dashed var(--line)',
          borderRadius: 8,
          background: '#fff',
          touchAction: 'none',
          cursor: 'crosshair',
        }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
      />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button
          className="btn-green"
          style={{ marginTop: 0 }}
          disabled={!hasStroke}
          onClick={() => onDone(canvasRef.current!.toDataURL('image/png'))}
        >
          この署名で確定
        </button>
        <button className="btn-line" onClick={clear}>
          書き直す
        </button>
        <button className="btn-line" onClick={onClose}>
          キャンセル
        </button>
      </div>
    </Modal>
  );
}
